import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp, ImagePlus, Mic, Square, X } from "lucide-react";
import { autocompletar, destacar } from "../lib/autocompletar";
import { useDitado } from "../hooks/Ditado";
import { useAnexoImagem } from "../hooks/AnexoImagem";
import { TIPOS_ACEITOS } from "../lib/lerImagem";

interface CampoPerguntaProps {
  id: string;
  valor: string;
  aoMudar: (valor: string) => void;
  // textoImagem: o que foi lido do print anexado (undefined quando não há print).
  aoEnviar: (texto: string, textoImagem?: string) => void;
  placeholder: string;
  desabilitado?: boolean;
  // Base do autocomplete (já na ordem de relevância).
  opcoes: string[];
  idioma: "pt" | "es";
  // No chat o campo fica embaixo, então a lista abre para cima.
  abrirPara?: "cima" | "baixo";
  // Algo a mais na linha das ferramentas, depois do microfone (o seletor de app, na tela inicial).
  extra?: React.ReactNode;
}

// Campo de pergunta com autocomplete, ditado por voz e anexo de print (vira texto). Fica dentro das caixas
// #box-input-duvida (tela inicial) e #box-input-duvida-conversa (chat).
const CampoPergunta: React.FC<CampoPerguntaProps> = ({
  id,
  valor,
  aoMudar,
  aoEnviar,
  placeholder,
  desabilitado = false,
  opcoes,
  idioma,
  abrirPara = "baixo",
  extra,
}) => {
  const { t } = useTranslation();
  const [aberto, setAberto] = useState(false);
  const [destacada, setDestacada] = useState(-1);
  const anexo = useAnexoImagem(idioma);
  const seletorArquivo = useRef<HTMLInputElement>(null);
  const temAnexo = anexo.estado.etapa !== "vazio";
  const lendoAnexo = anexo.estado.etapa === "lendo";

  const enviar = (texto: string) => {
    // Espera terminar a leitura do print, para ele não ficar de fora da pergunta.
    if (lendoAnexo) return;
    const textoImagem = anexo.estado.etapa === "pronto" ? anexo.estado.texto : undefined;
    if (!texto.trim() && textoImagem === undefined) return;
    setAberto(false);
    setDestacada(-1);
    aoEnviar(texto, textoImagem);
    if (temAnexo) anexo.remover();
  };

  const imagemDe = (arquivos?: FileList | null) =>
    Array.from(arquivos || []).find((arquivo) => arquivo.type.startsWith("image/"));

  // Ao terminar de falar, a pergunta é enviada sozinha (sem precisar de Enter).
  const ditado = useDitado(idioma, {
    aoTranscrever: (texto) => {
      aoMudar(texto);
      setAberto(false);
    },
    aoFinalizar: enviar,
  });

  const valorAntesDoDitado = useRef("");

  // Esc cancela o ditado sem enviar. Escuta no documento porque, depois do clique no
  // microfone, o foco está no botão e não no campo.
  useEffect(() => {
    if (!ditado.ouvindo) return;
    const aoTeclarDocumento = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") {
        ditado.cancelar();
        aoMudar(valorAntesDoDitado.current);
      }
    };
    document.addEventListener("keydown", aoTeclarDocumento);
    return () => document.removeEventListener("keydown", aoTeclarDocumento);
  }, [ditado.ouvindo]);

  const sugestoes = useMemo(() => autocompletar(valor, opcoes), [valor, opcoes]);
  const listaVisivel =
    aberto && !ditado.ouvindo && !temAnexo && sugestoes.length > 0 && !desabilitado;

  const aoTeclar = (evento: React.KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === "Enter" && ditado.ouvindo) {
      // Enter durante o ditado = "terminei": para de ouvir e o fim do ditado envia.
      evento.preventDefault();
      ditado.parar();
    } else if (evento.key === "ArrowDown" && listaVisivel) {
      evento.preventDefault();
      setDestacada((i) => (i + 1) % sugestoes.length);
    } else if (evento.key === "ArrowUp" && listaVisivel) {
      evento.preventDefault();
      setDestacada((i) => (i <= 0 ? sugestoes.length - 1 : i - 1));
    } else if (evento.key === "Escape") {
      setAberto(false);
    } else if (evento.key === "Enter") {
      enviar(listaVisivel && destacada >= 0 ? sugestoes[destacada] : valor);
    }
  };

  const alternarMicrofone = () => {
    if (ditado.ouvindo) {
      ditado.parar();
    } else {
      valorAntesDoDitado.current = valor;
      ditado.iniciar(valor);
    }
  };

  return (
    <>
      <input
        type="search"
        id={id}
        autoComplete="off"
        role="combobox"
        aria-expanded={listaVisivel}
        aria-controls={`${id}-sugestoes`}
        aria-activedescendant={destacada >= 0 ? `${id}-sugestao-${destacada}` : undefined}
        placeholder={ditado.ouvindo ? t("ouvindo") : placeholder}
        className="flex-grow"
        disabled={desabilitado}
        value={valor}
        onChange={(e) => {
          aoMudar(e.target.value);
          setAberto(true);
          setDestacada(-1);
        }}
        onFocus={() => setAberto(true)}
        onBlur={() => setAberto(false)}
        onKeyDown={aoTeclar}
        // Ctrl+V com um print copiado anexa a imagem.
        onPaste={(e) => {
          const imagem = imagemDe(e.clipboardData?.files);
          if (imagem) {
            e.preventDefault();
            anexo.anexar(imagem);
          }
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          const imagem = imagemDe(e.dataTransfer?.files);
          if (imagem) {
            e.preventDefault();
            anexo.anexar(imagem);
          }
        }}
      />

      <input
        ref={seletorArquivo}
        type="file"
        accept={TIPOS_ACEITOS.join(",")}
        hidden
        onChange={(e) => {
          const imagem = imagemDe(e.target.files);
          if (imagem) anexo.anexar(imagem);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        className="botao-microfone botao-anexo"
        onClick={() => seletorArquivo.current?.click()}
        disabled={desabilitado}
        aria-label={t("anexar_print")}
        title={t("anexar_print")}
      >
        <ImagePlus size={18} />
      </button>

      {ditado.disponivel && (
        <button
          type="button"
          className={`botao-microfone ${ditado.ouvindo ? "ouvindo" : ""}`}
          onClick={alternarMicrofone}
          disabled={desabilitado}
          aria-label={ditado.ouvindo ? t("parar_ditado") : t("falar_pergunta")}
          title={ditado.ouvindo ? t("parar_ditado") : t("falar_pergunta")}
        >
          {ditado.ouvindo ? <Square size={14} /> : <Mic size={18} />}
        </button>
      )}

      {extra}

      {/* Enviar: acende quando há texto ou print pronto para mandar. */}
      <button
        type="button"
        className="botao-enviar"
        onClick={() => enviar(valor)}
        disabled={desabilitado || lendoAnexo || (!valor.trim() && anexo.estado.etapa !== "pronto")}
        aria-label={t("enviar_pergunta")}
        title={t("enviar_pergunta")}
      >
        <ArrowUp size={17} strokeWidth={1.8} />
      </button>

      {temAnexo && (
        <div className={`painel-anexo painel-${abrirPara}`} role="region" aria-label={t("print_anexado")}>
          {"previa" in anexo.estado && anexo.estado.previa && (
            <img className="painel-anexo-previa" src={anexo.estado.previa} alt={t("print_anexado")} />
          )}
          <div className="painel-anexo-corpo">
            {anexo.estado.etapa === "lendo" && (
              <>
                <p className="painel-anexo-titulo">
                  {anexo.estado.fonte === "ia"
                    ? t("analisando_print")
                    : t("lendo_print", { porcentagem: Math.round(anexo.estado.progresso * 100) })}
                </p>
                <div className={`painel-anexo-barra ${anexo.estado.fonte === "ia" ? "indeterminada" : ""}`}>
                  <span
                    style={
                      anexo.estado.fonte === "ia"
                        ? undefined
                        : { width: `${Math.round(anexo.estado.progresso * 100)}%` }
                    }
                  />
                </div>
              </>
            )}
            {/* A conversão em texto fica escondida: a pessoa só vê que o print está pronto. */}
            {anexo.estado.etapa === "pronto" && (
              <p className="painel-anexo-titulo">
                {anexo.estado.texto ? t("print_pronto") : t("print_sem_texto")}
              </p>
            )}
            {anexo.estado.etapa === "erro" && (
              <p className="painel-anexo-titulo painel-anexo-erro">{t(`print_erro_${anexo.estado.motivo}`)}</p>
            )}
          </div>
          <button
            type="button"
            className="painel-anexo-remover"
            onClick={anexo.remover}
            aria-label={t("remover_print")}
            title={t("remover_print")}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {ditado.erro && !ditado.ouvindo && (
        <p className={`aviso-microfone aviso-${abrirPara}`} role="status">
          {t(`ditado_${ditado.erro}`)}
        </p>
      )}

      {listaVisivel && (
        <ul
          id={`${id}-sugestoes`}
          role="listbox"
          className={`lista-autocomplete lista-${abrirPara}`}
        >
          {sugestoes.map((sugestao, indice) => (
            <li
              key={sugestao}
              id={`${id}-sugestao-${indice}`}
              role="option"
              aria-selected={indice === destacada}
              className={indice === destacada ? "destacada" : ""}
              // mousedown em vez de click: o blur do input fecharia a lista antes do click
              onMouseDown={(e) => {
                e.preventDefault();
                enviar(sugestao);
              }}
              onMouseEnter={() => setDestacada(indice)}
            >
              {destacar(sugestao, valor).map((parte, i) =>
                parte.destaque ? <strong key={i}>{parte.trecho}</strong> : <span key={i}>{parte.trecho}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default CampoPergunta;
